import { Field, Input, Tooltip } from '@fluentui/react-components';
import { Dismiss20Regular } from '@fluentui/react-icons';
import React, {
  FC,
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { Button, ButtonProps } from '../Button';
import { Flex } from '../Flex';
import { GetConfirmationProps } from '../hooks/useConfirmationDialog';
import { ResponsiveDialog } from '../ResponsiveDialog/ResponsiveDialog';

export interface ConfirmationDialogInputValidationResult {
  valid: boolean;
  message: string | '';
}

export interface ConfirmationDialogProps {
  open: boolean;
  content: ReactNode | string;
  onConfirm(input?: string): void;
  onCancel(): void;
  headerText: string;
  confirmButtonText: string;
  cancelButtonText: string;
  showCloseButton: boolean;
  prompt?: boolean;
  validate?(input: string | undefined): ConfirmationDialogInputValidationResult;
}

export interface ConfirmationDialogProviderProps {
  content: ReactNode | string;
  actionCallback(x: boolean | PromiseLike<boolean> | string | PromiseLike<string>): void;
  headerText: string;
  confirmButtonText: string;
  cancelButtonText: string;
  showCloseButton: boolean;
  prompt?: boolean;
  validate?(input: string | undefined): ConfirmationDialogInputValidationResult;
}

const confirmationDialogProviderInital: ConfirmationDialogProviderProps = {
  cancelButtonText: '',
  headerText: '',
  confirmButtonText: '',
  content: '',
  actionCallback: () => {},
  showCloseButton: false,
};

export interface ConfirmationDialogContextProps {
  openDialog(value: ConfirmationDialogProviderProps): void;
  options: GetConfirmationProps;
}

export const ConfirmationDialogContext = React.createContext<
  Partial<ConfirmationDialogContextProps>
>({});

interface ConfirmationDialogProviderComponentProps {
  children: ReactNode;
}

export const ConfirmationDialogProvider: FC<ConfirmationDialogProviderComponentProps> = ({
  children,
}) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogConfig, setDialogConfig] = React.useState<ConfirmationDialogProviderProps>(
    confirmationDialogProviderInital,
  );

  const openDialog = (props: ConfirmationDialogProviderProps) => {
    const {
      headerText,
      content,
      actionCallback,
      cancelButtonText,
      confirmButtonText,
      showCloseButton,
      prompt,
      validate,
    } = props;
    setDialogOpen(true);
    setDialogConfig({
      headerText,
      content,
      actionCallback,
      cancelButtonText,
      confirmButtonText,
      showCloseButton,
      prompt,
      validate,
    });
  };

  const resetDialog = () => {
    setDialogOpen(false);
    setDialogConfig(confirmationDialogProviderInital);
  };

  const onConfirm = (input?: string) => {
    resetDialog();
    dialogConfig.actionCallback(input ?? true);
  };

  const onCancel = () => {
    resetDialog();
    dialogConfig.actionCallback(false);
  };

  return (
    <ConfirmationDialogContext.Provider value={{ openDialog }}>
      <ConfirmationDialog
        cancelButtonText={dialogConfig.cancelButtonText}
        confirmButtonText={dialogConfig.confirmButtonText}
        open={dialogOpen}
        headerText={dialogConfig.headerText}
        content={dialogConfig.content}
        onConfirm={onConfirm}
        onCancel={onCancel}
        showCloseButton={dialogConfig.showCloseButton}
        validate={dialogConfig.validate}
        prompt={dialogConfig.prompt}
      />
      {children}
    </ConfirmationDialogContext.Provider>
  );
};

/**
 * Button for the dialog actions that shows a tooltip with the full label when
 * the label is truncated. The action buttons truncate long text with an
 * ellipsis (see Button.scss `.aix-button__content`); this makes the hidden
 * text still discoverable on hover/focus, and adds nothing when it fits.
 */
const TruncationTooltipButton = forwardRef<HTMLButtonElement, ButtonProps & { label: string }>(
  ({ label, ...buttonProps }, ref) => {
    const contentRef = useRef<HTMLButtonElement | null>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const setRefs = useCallback(
      (node: HTMLButtonElement | null) => {
        contentRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    useLayoutEffect(() => {
      const element = contentRef.current?.querySelector<HTMLElement>('.aix-button__content');

      if (!element) {
        return;
      }

      const updateTruncation = () => {
        setIsTruncated(element.scrollWidth > element.clientWidth);
      };

      updateTruncation();

      const observer = new ResizeObserver(updateTruncation);
      observer.observe(element);

      return () => observer.disconnect();
    }, [label]);

    const button = (
      <Button {...buttonProps} ref={setRefs}>
        {label}
      </Button>
    );

    if (!isTruncated) {
      return button;
    }

    return (
      <Tooltip content={label} relationship="label" withArrow>
        {button}
      </Tooltip>
    );
  },
);

TruncationTooltipButton.displayName = 'TruncationTooltipButton';

const ConfirmationDialog: FC<ConfirmationDialogProps> = (props) => {
  const {
    open,
    cancelButtonText,
    onCancel,
    confirmButtonText,
    content,
    headerText,
    onConfirm,
    showCloseButton,
    prompt,
    validate,
  } = props;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  const [input, setInput] = useState<string | undefined>();
  const validationResult = validate?.(input);
  const error = validationResult?.message;
  const isValid = !validationResult || validationResult.valid;
  const handleConfirm = () => {
    if (!isValid) {
      return;
    }

    onConfirm(input);
    setInput(undefined);
  };

  const primaryButtonProps = {
    onClick: handleConfirm,
    appearance: 'primary' as const,
    disabled: !isValid,
    'data-testid': 'ConfirmationDialog-ConfirmButton',
  };

  const secondaryButtonProps = {
    onClick: onCancel,
    'data-testid': 'ConfirmationDialog-CancelButton',
  };

  useEffect(() => {
    if (prompt) {
      inputRef?.current?.focus();
    } else {
      confirmRef?.current?.focus();
    }
  });

  return (
    <ResponsiveDialog
      data-testid="ConfirmationDialog"
      open={open}
      primaryAction={
        <TruncationTooltipButton
          {...primaryButtonProps}
          ref={confirmRef}
          tabIndex={0}
          label={confirmButtonText}
        />
      }
      secondaryAction={
        <TruncationTooltipButton {...secondaryButtonProps} label={cancelButtonText} />
      }
      headerAction={
        showCloseButton ? (
          <Button
            appearance="transparent"
            icon={<Dismiss20Regular />}
            onClick={onCancel}
            style={{ justifyContent: 'end' }}
          />
        ) : undefined
      }
      header={headerText}
    >
      <Flex direction="column" gap="5px">
        {content}
        {prompt && (
          <Field
            validationMessage={!isValid ? error : undefined}
            validationState={!isValid ? 'error' : undefined}
          >
            <Input
              appearance="filled-darker"
              ref={inputRef}
              value={input ?? ''}
              onChange={(_, data) => {
                setInput(data?.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleConfirm();
                }
              }}
            />
          </Field>
        )}
      </Flex>
    </ResponsiveDialog>
  );
};

export default ConfirmationDialog;
